import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useCreateIdeaSubmissionMutation } from "../../api/ideaSubmission/ideaSubmissionApiSlice";

// Mirrors the backend's own bound (ideaSubmissionController.MAX_TEXT_LENGTH).
// Duplicated because a TS frontend cannot import an ECS service's constant;
// the two name each other so a change to one goes looking for the other.
const MAX_TEXT_LENGTH = 4000;

const MAX_EMAIL_LENGTH = 255;

/**
 * Deliberately permissive, and the same rule the backend applies.
 *
 * Scanning code rather than a regex: the obvious pattern backtracks
 * super-linearly, which is a liability on an input someone can paste into.
 *
 * MIRRORED from `new-backend/app/src/controllers/ideaSubmissionController.ts`
 * (`isPlausibleEmail`) — the two deployables cannot import from each other.
 * Change one, change the other; both are pinned by tests.
 */
export function isPlausibleEmail(value: string): boolean {
    if (value.length === 0 || value.length > MAX_EMAIL_LENGTH) return false;
    for (const ch of value) {
        if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") return false;
    }
    const at = value.indexOf("@");
    if (at <= 0 || value.indexOf("@", at + 1) !== -1) return false;
    const domain = value.slice(at + 1);
    const dot = domain.indexOf(".");
    return dot > 0 && dot < domain.length - 1;
}

/**
 * Inline "send us your idea" form on the "more to come" page.
 *
 * Replaces the mailto-only path: the page text still resolves `{email}` for
 * tenants whose copy mentions it, but the visitor can submit without leaving
 * the app. Posts to `/app/idea-submissions` on the new backend.
 */
const IdeaSubmissionForm = () => {
    const { t } = useTranslation();
    const [text, setText] = useState("");
    const [email, setEmail] = useState("");
    // Client-side validation message, kept apart from the server's.
    const [validationError, setValidationError] = useState<string | null>(null);
    const [serverError, setServerError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);

    const [createIdeaSubmission, { isLoading }] = useCreateIdeaSubmissionMutation();

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setValidationError(null);
        setServerError(null);

        if (text.trim().length === 0) {
            setValidationError(t("idea_submission.error_required"));
            return;
        }
        if (email.trim().length > 0 && !isPlausibleEmail(email.trim())) {
            setValidationError(t("idea_submission.error_email"));
            return;
        }

        try {
            // `.unwrap()` so a rejected mutation throws here rather than
            // resolving into something that reads like success — a flag meaning
            // both "finished" and "succeeded" is the #2218/#2221 trap.
            await createIdeaSubmission({
                text: text.trim(),
                contactEmail: email.trim() || undefined,
            }).unwrap();
            setSubmitted(true);
        } catch (err) {
            const status = (err as { status?: number })?.status;
            if (status === 501) {
                // The tenant has no idea_submission table (legacy store).
                setServerError(t("idea_submission.error_unavailable"));
                return;
            }
            // Prefer the server's own message where it sent one — one generic
            // string for every cause is what cost three sessions a round.
            const data = (err as { data?: unknown })?.data;
            const serverMessage =
                data && typeof data === "object" && typeof (data as { message?: unknown }).message === "string"
                    ? (data as { message: string }).message
                    : null;
            setServerError(serverMessage ?? t("idea_submission.error_generic"));
        }
    };

    if (submitted) {
        return (
            <p
                role="status"
                className="text-center text-base leading-6 text-foreground mt-6"
            >
                {t("idea_submission.success")}
            </p>
        );
    }

    const errorMessage = validationError ?? serverError;

    return (
        <form onSubmit={handleSubmit} className="mt-8 text-left" noValidate>
            {/* No separate heading — the promoted field label leads the form
                (PO, 2026-09-16: "Idee senden" above the field was one label
                too many; the button carries the action wording instead). */}
            <label
                htmlFor="idea-submission-text"
                className="block text-base font-semibold text-foreground mb-2"
            >
                {t("idea_submission.text_label")}
            </label>
            <textarea
                id="idea-submission-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={MAX_TEXT_LENGTH}
                rows={5}
                placeholder={t("idea_submission.text_placeholder")}
                aria-invalid={Boolean(validationError)}
                className="flex w-full rounded-xl border border-black bg-white px-4 py-3 text-base text-slate-900 placeholder:text-slate-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoading}
            />

            <label
                htmlFor="idea-submission-email"
                className="block text-base font-semibold text-foreground mt-6 mb-2"
            >
                {t("idea_submission.email_label")}
            </label>
            <Input
                id="idea-submission-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
                placeholder={t("idea_submission.email_placeholder")}
                disabled={isLoading}
            />
            <p className="text-sm text-foreground/60 mt-2">
                {t("idea_submission.email_hint")}
            </p>

            {errorMessage && (
                <p role="alert" className="text-sm text-appRed mt-4">
                    {errorMessage}
                </p>
            )}

            <Button type="submit" disabled={isLoading} className="w-full mt-6">
                {isLoading ? t("idea_submission.submitting") : t("idea_submission.submit")}
            </Button>
        </form>
    );
};

export default IdeaSubmissionForm;
